import React, { useCallback, useEffect, useRef, useState } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────

export type ConfirmVariant = 'warning' | 'danger' | 'info';

export interface ConfirmConfig {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// ── Visual theme per variant ───────────────────────────────────────────────

const theme: Record<ConfirmVariant, { bg: string; border: string; accent: string; text: string; icon: string }> = {
  warning: { bg: '#fffbeb', border: '#fcd34d', accent: '#d97706', text: '#92400e', icon: '⚠' },
  danger:  { bg: '#fff1f2', border: '#fca5a5', accent: '#dc2626', text: '#991b1b', icon: '⊘' },
  info:    { bg: '#eff6ff', border: '#93c5fd', accent: '#2563eb', text: '#1e40af', icon: 'ℹ' },
};

// ── Confirm component ──────────────────────────────────────────────────────

export default function Confirm({ config }: { config: ConfirmConfig | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (config) {
      setVisible(true);
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 30);
    } else {
      setVisible(false);
    }
  }, [config]);

  if (!config || !visible) return null;

  const t = theme[config.variant ?? 'warning'];

  return (
    <div
      ref={ref}
      role="alertdialog"
      aria-live="assertive"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 14,
        padding: '16px 20px', borderRadius: 10,
        border: `1px solid ${t.border}`, background: t.bg,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        animation: 'slideDown 0.2s ease',
      }}
    >
      {/* Icon badge */}
      <div style={{
        width: 32, height: 32, borderRadius: '50%', background: t.accent,
        color: '#fff', fontSize: 15, fontWeight: 700, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {t.icon}
      </div>

      {/* Body */}
      <div style={{ flex: 1 }}>
        {config.title && (
          <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: t.text }}>
            {config.title}
          </p>
        )}
        <p style={{ margin: 0, fontSize: 13, color: t.text, lineHeight: 1.5 }}>
          {config.message}
        </p>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
        <button
          style={s.btnCancel}
          onClick={config.onCancel}
          disabled={config.loading}
        >
          {config.cancelLabel ?? 'Cancelar'}
        </button>
        <button
          style={{ ...s.btnConfirm, background: t.accent }}
          onClick={config.onConfirm}
          disabled={config.loading}
        >
          {config.loading ? 'Procesando…' : (config.confirmLabel ?? 'Confirmar')}
        </button>
      </div>
    </div>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useConfirm() {
  const [config, setConfig] = useState<ConfirmConfig | null>(null);

  const ask = (
    message: React.ReactNode,
    onConfirm: () => void,
    opts?: {
      title?: string;
      confirmLabel?: string;
      cancelLabel?: string;
      variant?: ConfirmVariant;
      skipLoading?: boolean;
      onCancel?: () => void;
    },
  ) => {
    setConfig({
      message,
      variant:      opts?.variant ?? 'warning',
      title:        opts?.title,
      confirmLabel: opts?.confirmLabel,
      cancelLabel:  opts?.cancelLabel,
      onConfirm: () => {
        if (!opts?.skipLoading) {
          setConfig(prev => prev ? { ...prev, loading: true } : null);
        }
        onConfirm();
      },
      onCancel: () => {
        setConfig(null);
        opts?.onCancel?.();
      },
    });
  };

  const dismiss = () => setConfig(null);

  const setConfirmLoading = useCallback((v: boolean) => {
    setConfig(prev => {
      if (!prev || prev.loading === v) return prev;
      return { ...prev, loading: v };
    });
  }, []);

  return { confirmConfig: config, ask, dismiss, setConfirmLoading };
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  btnCancel: {
    padding: '7px 16px', background: '#fff', color: '#475569',
    border: '1px solid #e2e8f0', borderRadius: 7,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  btnConfirm: {
    padding: '7px 16px', color: '#fff', border: 'none',
    borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
};
